import { Link } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { useBuildStore } from '@/store/build-store'
import { useConfigStore } from '@/store/config-store'
import { formatCurrency } from '@/lib/pricing'
import { calculateTotal } from '@/lib/pricing'

const STEPS = ['model', 'customize', 'review', 'form'] as const
const STEP_LABELS = ['Model', 'Customize', 'Review', 'Quote']

interface Props {
  variant?: 'builder' | 'admin' | 'plain'
  onMenuToggle?: () => void
}

export function Header({ variant = 'plain', onMenuToggle }: Props) {
  const { currentStep, selectedModelId, selectedOptionIds, resetBuild } = useBuildStore()
  const { config } = useConfigStore()

  const stepIndex = STEPS.indexOf(currentStep as typeof STEPS[number])
  const total = config
    ? calculateTotal(selectedModelId, selectedOptionIds, config.models, config.options)
    : 0

  return (
    <header className="sticky top-0 z-50 bg-dark-900 border-b border-dark-700">
      <div className="max-w-screen-xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="h-8 w-8 bg-brand-500 rounded-md flex items-center justify-center">
            <span className="text-white font-bold text-sm leading-none">L&M</span>
          </div>
          <span className="text-white font-semibold text-sm hidden sm:block">Custom Builders</span>
        </Link>

        {/* Builder progress stepper */}
        {variant === 'builder' && (
          <div className="hidden md:flex items-center gap-1">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex items-center gap-1">
                <div
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    i < stepIndex
                      ? 'bg-brand-500/20 text-brand-400'
                      : i === stepIndex
                      ? 'bg-brand-500 text-white'
                      : 'text-slate-500'
                  }`}
                >
                  <span
                    className={`h-4 w-4 rounded-full text-xs flex items-center justify-center font-bold ${
                      i < stepIndex
                        ? 'bg-brand-500 text-white'
                        : i === stepIndex
                        ? 'bg-white/20'
                        : 'bg-dark-700 text-slate-500'
                    }`}
                  >
                    {i < stepIndex ? '✓' : i + 1}
                  </span>
                  {label}
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div className={`h-px w-4 ${i < stepIndex ? 'bg-brand-500' : 'bg-dark-600'}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Right side */}
        <div className="flex items-center gap-3">
          {variant === 'builder' && total > 0 && (
            <span className="text-brand-400 font-semibold text-sm hidden sm:block">
              {formatCurrency(total)}
            </span>
          )}
          {variant === 'builder' && (
            <button
              onClick={resetBuild}
              className="text-slate-500 hover:text-slate-300 text-xs transition-colors"
            >
              Restart
            </button>
          )}
          {variant === 'admin' && (
            <>
              <span className="text-slate-400 text-xs hidden sm:block">Admin Dashboard</span>
              {onMenuToggle && (
                <button
                  onClick={onMenuToggle}
                  className="md:hidden text-slate-400 hover:text-white transition-colors"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  )
}
